import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Users, FileText, CheckCircle, DollarSign, TrendingUp,
  CalendarDays, Plus, ArrowRight, Columns3
} from "lucide-react";

const STAT_CARDS = [
  { key: "total_leads", label: "Total Leads", icon: Users, color: "text-blue-600", bg: "bg-blue-50" },
  { key: "estimates_sent", label: "Estimates Sent", icon: FileText, color: "text-orange-600", bg: "bg-orange-50" },
  { key: "jobs_won", label: "Jobs Won", icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "revenue", label: "Revenue", icon: DollarSign, color: "text-green-600", bg: "bg-green-50", isCurrency: true },
  { key: "conversion_rate", label: "Conversion Rate", icon: TrendingUp, color: "text-purple-600", bg: "bg-purple-50", suffix: "%" },
  { key: "scheduled_this_week", label: "Scheduled This Week", icon: CalendarDays, color: "text-cyan-600", bg: "bg-cyan-50" },
];

const STAGE_COLORS = {
  new_lead: "bg-blue-100 text-blue-700",
  contacted: "bg-violet-100 text-violet-700",
  estimate_scheduled: "bg-amber-100 text-amber-700",
  estimate_sent: "bg-orange-100 text-orange-700",
  waiting_approval: "bg-red-100 text-red-700",
  approved: "bg-emerald-100 text-emerald-700",
  scheduled: "bg-cyan-100 text-cyan-700",
  in_progress: "bg-orange-100 text-orange-700",
  completed: "bg-green-100 text-green-700",
  paid: "bg-green-200 text-green-800",
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(err => console.error("Failed to load stats:", err));
    api.get("/leads").then((r) => setRecentLeads(r.data.slice(0, 5))).catch(err => console.error("Failed to load leads:", err));
  }, []);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 tracking-tight">Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-1">Your business at a glance</p>
        </div>
        <Button
          onClick={() => navigate("/pipeline?new=true")}
          className="bg-orange-600 hover:bg-orange-700 text-white"
          data-testid="dashboard-add-lead-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Lead
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {STAT_CARDS.map((card, i) => (
          <Card
            key={card.key}
            className="border border-zinc-200 shadow-none animate-fade-in"
            style={{ animationDelay: `${i * 0.05}s` }}
            data-testid={`stat-${card.key}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{card.label}</p>
                  <p className="text-2xl sm:text-3xl font-extrabold text-zinc-900 mt-1">
                    {stats ? (
                      card.isCurrency ? `$${(stats[card.key] || 0).toLocaleString()}` :
                      `${stats[card.key] || 0}${card.suffix || ""}`
                    ) : "..."}
                  </p>
                </div>
                <div className={`${card.bg} p-2 rounded-lg`}>
                  <card.icon className={`w-5 h-5 ${card.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pipeline Overview & Recent Leads */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Pipeline Overview */}
        <Card className="border border-zinc-200 shadow-none" data-testid="pipeline-overview">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold text-zinc-900">Pipeline Overview</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/pipeline")} className="text-orange-600 hover:text-orange-700 text-xs">
                View All <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {stats?.stage_counts && Object.entries(stats.stage_counts).map(([stage, count]) => (
              <div key={stage} className="flex items-center justify-between py-1.5">
                <span className="text-sm text-zinc-600 capitalize">{stage.replace(/_/g, " ")}</span>
                <Badge variant="secondary" className={`text-xs ${STAGE_COLORS[stage] || "bg-zinc-100 text-zinc-600"}`}>
                  {count}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Leads */}
        <Card className="border border-zinc-200 shadow-none" data-testid="recent-leads">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold text-zinc-900">Recent Leads</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/search")} className="text-orange-600 hover:text-orange-700 text-xs">
                View All <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentLeads.length === 0 ? (
              <div className="text-center py-8">
                <Columns3 className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No leads yet. Add your first lead!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentLeads.map((lead) => (
                  <button
                    key={lead.id}
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    className="w-full flex items-center justify-between p-3 rounded-lg border border-zinc-100 hover:border-orange-300 hover:bg-orange-50/30 transition-colors text-left"
                    data-testid={`recent-lead-${lead.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{lead.name}</p>
                      <p className="text-xs text-zinc-500 truncate">{lead.phone || lead.email || "No contact"}</p>
                    </div>
                    <Badge variant="secondary" className={`text-xs ml-2 flex-shrink-0 ${STAGE_COLORS[lead.stage] || ""}`}>
                      {lead.stage?.replace(/_/g, " ")}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
